import { defineWidget } from '../../widget-types';
import type { BlockNode, BlockGroupConfig } from '../../block-types';

/**
 * Default blocks for the Exogro footer — decomposed into 3 rows.
 * Each block has an explicit `layout.groupId` so the centralized
 * GenericGroupedBlockLayout can render them without ID-prefix fallback.
 */
const exogroFooterDefaultBlocks: BlockNode[] = [
  // ── Row 1: Main footer — Column A (col-span-5): Logo + tagline ──
  {
    id: 'footer-logo',
    type: 'image',
    content: {
      type: 'image',
      src: '/exogro-logo.png',
      alt: 'Exogro',
      objectFit: 'contain',
    },
    layout: { order: 0, widthPct: 100, heightPx: 140 , groupId: 'footer' },
  },
  {
    id: 'footer-tagline',
    type: 'text',
    content: {
      type: 'text',
      value: 'ผู้ให้บริการจัดซื้อจัดหาสินค้าอุตสาหกรรมครบวงจร',
      fontSize: 18,
      fontWeight: 700,
      color: '#111827',
    },
    layout: { order: 1, widthPct: 100 , groupId: 'footer' },
  },

  // ── Row 1: Column B (col-span-4): About heading + links ──
  {
    id: 'footer-about-heading',
    type: 'text',
    content: {
      type: 'text',
      value: 'เกี่ยวกับเรา',
      fontSize: 16,
      fontWeight: 700,
      color: '#111827',
    },
    layout: { order: 0, widthPct: 100 , groupId: 'footer' },
  },
  // About links (left column of 2-col sub-grid)
  {
    id: 'footer-link-quote',
    type: 'button',
    content: {
      type: 'button',
      label: 'วิธีขอใบเสนอราคา',
      href: '/pages/quote',
      variant: 'ghost',
      size: 'sm',
    },
    layout: { order: 1, widthPct: 50 , groupId: 'footer' },
  },
  {
    id: 'footer-link-why',
    type: 'button',
    content: {
      type: 'button',
      label: 'ทำไมต้อง Exogro',
      href: '/pages/why-exogro',
      variant: 'ghost',
      size: 'sm',
    },
    layout: { order: 2, widthPct: 50 , groupId: 'footer' },
  },
  {
    id: 'footer-link-blog',
    type: 'button',
    content: {
      type: 'button',
      label: 'บทความ',
      href: '/pages/blog',
      variant: 'ghost',
      size: 'sm',
    },
    layout: { order: 3, widthPct: 50 , groupId: 'footer' },
  },
  {
    id: 'footer-link-careers',
    type: 'button',
    content: {
      type: 'button',
      label: 'สมัครงาน',
      href: '/pages/careers',
      variant: 'ghost',
      size: 'sm',
    },
    layout: { order: 4, widthPct: 50 , groupId: 'footer' },
  },
  // Legal links (right column of 2-col sub-grid)
  {
    id: 'footer-link-privacy',
    type: 'button',
    content: {
      type: 'button',
      label: 'Privacy Policy',
      href: '/pages/privacy-policy',
      variant: 'ghost',
      size: 'sm',
    },
    layout: { order: 5, widthPct: 50 , groupId: 'footer' },
  },
  {
    id: 'footer-link-terms',
    type: 'button',
    content: {
      type: 'button',
      label: 'Terms of Condition',
      href: '/pages/terms',
      variant: 'ghost',
      size: 'sm',
    },
    layout: { order: 6, widthPct: 50 , groupId: 'footer' },
  },

  // ── Row 1: Column C (col-span-3): Contact heading + address + social ──
  {
    id: 'footer-contact-heading',
    type: 'text',
    content: {
      type: 'text',
      value: 'ติดต่อเรา',
      fontSize: 16,
      fontWeight: 700,
      color: '#111827',
    },
    layout: { order: 0, widthPct: 100 , groupId: 'footer' },
  },
  {
    id: 'footer-address',
    type: 'text',
    content: {
      type: 'text',
      value: '8/5 หมู่ 3 ต.บางแม่บาง อ.บางใหญ่ จ.นนทบุรี 11140',
      fontSize: 14,
      color: '#4B5563',
    },
    layout: { order: 1, widthPct: 100 , groupId: 'footer' },
  },
  {
    id: 'footer-social-facebook',
    type: 'social-link',
    content: {
      type: 'social-link',
      platform: 'facebook',
      url: 'https://www.facebook.com/ExogroCoLtd',
    },
    layout: { order: 2, widthPct: 33 , groupId: 'footer' },
  },
  {
    id: 'footer-social-line',
    type: 'social-link',
    content: {
      type: 'social-link',
      platform: 'line',
      url: 'https://line.me/R/ti/p/@exogro',
    },
    layout: { order: 3, widthPct: 33 , groupId: 'footer' },
  },
  {
    id: 'footer-social-youtube',
    type: 'social-link',
    content: {
      type: 'social-link',
      platform: 'youtube',
      url: 'https://www.youtube.com/@exogro',
    },
    layout: { order: 4, widthPct: 33 , groupId: 'footer' },
  },

  // ── Row 2: Contact bar (brandColor background) ──
  {
    id: 'contact-phone',
    type: 'contact-item',
    content: {
      type: 'contact-item',
      contactType: 'phone',
      value: '02-103-9000',
    },
    layout: { order: 0, widthPct: 25 , groupId: 'contact' },
  },
  {
    id: 'contact-fax',
    type: 'contact-item',
    content: {
      type: 'contact-item',
      contactType: 'fax',
      value: '02-103-9001',
    },
    layout: { order: 1, widthPct: 25 , groupId: 'contact' },
  },
  {
    id: 'contact-email',
    type: 'contact-item',
    content: {
      type: 'contact-item',
      contactType: 'email',
      value: 'sales@exogro.co.th',
    },
    layout: { order: 2, widthPct: 25 , groupId: 'contact' },
  },
  {
    id: 'contact-line',
    type: 'contact-item',
    content: {
      type: 'contact-item',
      contactType: 'line',
      value: '@exogro',
    },
    layout: { order: 3, widthPct: 25 , groupId: 'contact' },
  },

  // ── Row 3: Copyright bar ──
  {
    id: 'copyright-text',
    type: 'text',
    content: {
      type: 'text',
      value: '© 2026 Exogro. All Rights Reserved',
      fontSize: 12,
      color: '#6B7280',
      textAlign: 'center',
    },
    layout: { order: 0, widthPct: 100 , groupId: 'copyright' },
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// BLOCK GROUP CONFIG (declarative layout for GenericGroupedBlockLayout)
// ═══════════════════════════════════════════════════════════════════════════

const exogroFooterBlockGroups: BlockGroupConfig[] = [
  {
    id: 'footer',
    label: 'Main Footer',
    direction: 'row',
    containerClass: 'bg-white py-10',
    innerClass: 'mx-auto grid max-w-7xl grid-cols-12 gap-8 px-4',
    allowedTypes: ['text', 'image', 'button', 'social-link'],
    naturalSizing: true,
  },
  {
    id: 'contact',
    label: 'Contact Bar',
    direction: 'row',
    containerClass: 'py-4',
    innerClass: 'mx-auto flex max-w-7xl items-center justify-around px-4',
    allowedTypes: ['contact-item'],
    naturalSizing: true,
  },
  {
    id: 'copyright',
    label: 'Copyright Bar',
    direction: 'row',
    containerClass: 'border-t bg-gray-50 py-4',
    innerClass: 'mx-auto max-w-7xl px-4 text-center',
    allowedTypes: ['text'],
    naturalSizing: true,
  },
];

export const exogroFooterWidget = defineWidget({
  type: 'code:exogro-footer',
  schema: {
    label: 'Exogro Footer',
    description: 'Industrial supply footer with contact info, social links, and brand bar',
    props: {
      logoUrl: {
        type: 'image',
        label: 'Logo',
        default: '/exogro-logo.png',
      },
      tagline: {
        type: 'string',
        label: 'Tagline',
        default: 'ผู้ให้บริการจัดซื้อจัดหาสินค้าอุตสาหกรรมครบวงจร',
      },
      address: {
        type: 'string',
        label: 'Address',
        default: '8/5 หมู่ 3 ต.บางแม่บาง อ.บางใหญ่ จ.นนทบุรี 11140',
      },
      phone: {
        type: 'string',
        label: 'Phone',
        default: '02-103-9000',
      },
      fax: {
        type: 'string',
        label: 'Fax',
        default: '02-103-9001',
      },
      email: {
        type: 'string',
        label: 'Email',
        default: 'sales@exogro.co.th',
      },
      lineId: {
        type: 'string',
        label: 'LINE ID',
        default: '@exogro',
      },
      facebookUrl: {
        type: 'string',
        label: 'Facebook URL',
        default: 'https://www.facebook.com/ExogroCoLtd',
      },
      youtubeUrl: {
        type: 'string',
        label: 'YouTube URL',
        default: 'https://www.youtube.com/@exogro',
      },
      brandColor: {
        type: 'color',
        label: 'Brand Color',
        default: '#FF6600',
      },
      companyName: {
        type: 'string',
        label: 'Company Name',
        default: 'Exogro',
      },
    },
    slots: {},
    dataDeps: [],
  },
  nameTh: 'ฟุตเตอร์ Exogro',
  descriptionTh: 'ฟุตเตอร์สำหรับร้านค้าอุตสาหกรรม พร้อมข้อมูลติดต่อ โซเชียลมีเดีย และแถบแบรนด์',
  category: 'templates',
  thumbnail: '/widget-thumbnails/code/exogro-footer.png',
  defaults: {
    type: 'code:exogro-footer',
    props: {
      logoUrl: '/exogro-logo.png',
      tagline: 'ผู้ให้บริการจัดซื้อจัดหาสินค้าอุตสาหกรรมครบวงจร',
      address: '8/5 หมู่ 3 ต.บางแม่บาง อ.บางใหญ่ จ.นนทบุรี 11140',
      phone: '02-103-9000',
      fax: '02-103-9001',
      email: 'sales@exogro.co.th',
      lineId: '@exogro',
      facebookUrl: 'https://www.facebook.com/ExogroCoLtd',
      youtubeUrl: 'https://www.youtube.com/@exogro',
      brandColor: '#FF6600',
      companyName: 'Exogro',
    },
  },
  defaultBlocks: exogroFooterDefaultBlocks,
  blockGroups: exogroFooterBlockGroups,
});
